import { CSSProperties, useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';
import ModalSymphony from 'elements/ModalSymphony';
import ButtonSymphony from 'elements/ButtonSymphony';
import useOrganizationV2 from 'Hooks/useOrganizationV2';
import usePlans from 'Hooks/usePlans';
import SetupPaymentMethod from './SetupPaymentMethod';
import { SHARED_TOAST_OPTIONS_ERROR } from 'modules/Toast';
import Icon from 'elements/Icon';
import Colors from 'modules/Colors';
import Sty from 'modules/Sty';
import useUser from 'Hooks/useUser';
import { PlanType } from 'modules/Const';
import { Body2, Subtitle1 } from './Typography';

interface TeamTrialExpiredModalProps {
  /** Whether to show the modal automatically when trial expiry is detected */
  autoShow?: boolean;
  /** Manual control over modal visibility - overrides autoShow when provided */
  isOpen?: boolean;
  /** Callback when modal is closed */
  onClose?: () => void;
}

const TeamTrialExpiredModal = ({
  autoShow = true,
  isOpen,
  onClose,
}: TeamTrialExpiredModalProps) => {
  const { organization } = useOrganizationV2();
  const { getMonthlyPrice, fetchPlans, isInitialized } = usePlans();
  const { isSymphonyAdmin } = useUser();
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [hasShownThisSession, setHasShownThisSession] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);

  // Check if trial has expired
  const getIsTrialExpired = (): boolean => {
    if (!organization?.trial_end_date) return false;
    
    const trialEndDate = new Date(organization.trial_end_date);
    const now = new Date();
    return now > trialEndDate;
  };

  // Auto-show logic when isOpen is not manually controlled
  useEffect(() => {
    if (isOpen !== undefined) {
      return; // Manual control takes precedence
    }

    // Only show modal if:
    // 1. autoShow is enabled
    // 2. Trial has expired
    // 3. Haven't shown it this session yet
    // 4. Organization exists and has a trial
    // 5. User is not an admin (admins shouldn't be blocked)
    if (autoShow && getIsTrialExpired() && !hasShownThisSession && organization && !isSymphonyAdmin) {
      setShowModal(true);
      setHasShownThisSession(true);
    }
  }, [autoShow, organization, hasShownThisSession, isOpen, isSymphonyAdmin]);

  const handleAddPaymentMethod = () => {
    setShowPaymentForm(true);
  };

  const handlePaymentSuccess = () => {
    setShowPaymentForm(false);
    handleCloseModal();
    toast.success('Payment method added successfully! Your team subscription is now active.');
    // Refresh the page to update organization data
    window.location.reload();
  };

  const handlePaymentError = (error: string) => {
    Sentry.captureException(new Error(error));
    console.error('Payment error:', error);
    toast.error('Something went wrong adding your payment method. Please try again.', SHARED_TOAST_OPTIONS_ERROR);
    setShowPaymentForm(false);
  };

  const handleCancelTeam = async () => {
    if (!organization?.id) {
      toast.error('Unable to cancel team. Please try again.', SHARED_TOAST_OPTIONS_ERROR);
      return;
    }

    try {
      setIsLoading(true);
      
      // TODO: Implement team cancellation API
      // This would:
      // 1. Set organization setup_status to 'disbanded'
      // 2. Move all brands back to FREE tier
      // 3. Remove user_brands access for non-owners
      // 4. Cancel Stripe subscription
      
      toast.success('Team cancelled successfully. Your brands have been moved to individual accounts.');
      handleCloseModal();
      
      // Redirect to dashboard
      window.location.href = '/dashboard';
    } catch (error) {
      setIsLoading(false);
      Sentry.captureException(error);
      console.error('Error cancelling team:', error);
      toast.error('Something went wrong cancelling your team. Please contact support.', SHARED_TOAST_OPTIONS_ERROR);
    }
  };

  const handleCloseModal = () => {
    if (onClose) {
      onClose();
    }
    setShowModal(false);
    setShowPaymentForm(false);
  };

  // Use manual control if provided, otherwise use auto-show state
  const modalIsOpen = isOpen !== undefined ? isOpen : showModal;

  const renderBrandsList = () => {
    const brands = organization?.brands || [];
    if (brands.length === 0) return null;

    return (
      <div style={sty.brandsList}>
        <Body2 style={sty.sectionTitle}>Brands in your team:</Body2>
        <div style={sty.brandsGrid}>
          {brands.slice(0, 6).map((brand, index) => (
            <div key={brand.id || index} style={sty.brandItem}>
              <div 
                style={{
                  ...sty.brandAvatar,
                  backgroundImage: brand.image ? `url(${brand.image})` : 'none'
                }}
              >
                {!brand.image && <Icon className="userIcon" icon="user" size={16} color={Colors.greyDark} />}
              </div>
              <Body2 style={sty.brandName}>{brand.name}</Body2>
            </div>
          ))}
          {brands.length > 6 && (
            <div style={sty.brandItem}>
              <div style={sty.brandAvatar}>
                <Body2 style={sty.moreCount}>+{brands.length - 6}</Body2>
              </div>
              <Body2 style={sty.brandName}>more</Body2>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderUsersList = () => {
    // Note: users data structure needs to be defined in OrganizationData type
    // For now, we'll comment this out until the backend provides user data
    return null;
  };

  const renderMainContent = () => {
    if (showPaymentForm) {
      return (
        <div style={sty.paymentFormContainer}>
          <Body2 style={sty.paymentDescription}>
            Adding a payment method will activate your Team subscription at ${getMonthlyPrice(PlanType.TEAM_V2) || 199}/month.
          </Body2>
          
          <SetupPaymentMethod
            organizationId={organization?.id || 0}
            onSuccess={handlePaymentSuccess}
            onError={handlePaymentError}
            onCancel={() => setShowPaymentForm(false)}
            submitButtonText="Add Payment Method"
            showCancelButton={true}
          />
        </div>
      );
    }

    return (
      <div style={sty.mainContent}>
        <div style={sty.header}>
          <Icon className="warningIcon" icon="warning" size={36} color={Colors.red} />
          <Subtitle1 style={sty.title}>Your team plan trial has ended!</Subtitle1>
        </div>
        
        <div style={sty.body}>
          <Body2 style={sty.description}>
            Please add a payment method to continue using your team features, or cancel your team to move your brands back to individual accounts.
          </Body2>

          {renderBrandsList()}
          {renderUsersList()}
        </div>
        
        <div style={sty.actions}>
          <ButtonSymphony
            className="addPaymentMethodButton"
            onClick={handleAddPaymentMethod}
            variant="primary"
            color="purple"
            style={sty.primaryButton}
          >
            Add Payment Method
          </ButtonSymphony>

          <ButtonSymphony
            className="cancelTeamButton"
            onClick={handleCancelTeam}
            variant="outlined"
            color="red"
            isLoading={isLoading}
            disabled={isLoading}
            style={sty.secondaryButton}
          >
            Cancel Team
          </ButtonSymphony>
        </div>
      </div>
    );
  };

  return (
    <ModalSymphony
      size="medium"
      className="teamTrialExpiredModal"
      isOpen={modalIsOpen}
      onClose={handleCloseModal}
      isHideCloseButton={!isSymphonyAdmin} // Don't allow closing for non-admins
    >
      {renderMainContent()}
    </ModalSymphony>
  );
};

const sty: Record<string, CSSProperties> = {
  mainContent: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 24
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    justifyContent: 'center',
    flexDirection: 'column',
    textAlign: 'center'
  },
  title: {
    fontSize: 24,
    fontWeight: 600,
    color: Colors.red,
    margin: 0
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20
  },
  description: {
    fontSize: 16,
    lineHeight: '1.5',
    color: Colors.greyDark,
    textAlign: 'center'
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: Colors.black,
    marginBottom: 8
  },
  brandsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8
  },
  brandsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 12
  },
  brandItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: 8,
    backgroundColor: Colors.greyLight,
    borderRadius: 8
  },
  brandAvatar: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    backgroundColor: Colors.greyLight,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundSize: 'cover',
    backgroundPosition: 'center'
  },
  brandName: {
    fontSize: 14,
    color: Colors.black,
    fontWeight: 500
  },
  moreCount: {
    fontSize: 12,
    color: Colors.greyDark,
    fontWeight: 600
  },
  usersList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8
  },
  usersGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4
  },
  userItem: {
    padding: 6,
    backgroundColor: Colors.greyLight,
    borderRadius: 4
  },
  userEmail: {
    fontSize: 14,
    color: Colors.greyDark
  },
  actions: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    alignItems: 'center'
  },
  primaryButton: {
    width: '100%',
    padding: '12px 24px',
    fontSize: 16,
    fontWeight: 600
  },
  secondaryButton: {
    width: '100%',
    fontSize: 14
  },
  paymentFormContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24
  },
  paymentDescription: {
    fontSize: 16,
    color: Colors.greyDark,
    textAlign: 'center'
  }
};

export default TeamTrialExpiredModal;
